import * as Sentry from '@sentry/browser'
import { signInWithCustomToken } from 'firebase/auth'
import { collection, deleteField, doc, getDocs, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore'
import posthog from 'posthog-js'
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useErrorData } from 'src/context/ErrorContext'
import { auth, clearList, db, fetchFirebaseData, fetchOrgConfig, setAllAccessLevels } from 'src/services/firebase-compat'
import { metricsService } from 'src/services/metrices'
import { vitallyManager } from 'src/services/VitallyManager'

import { get, isEqual } from 'src/utils/config/lodashUtils'

import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { getSession, setSession } from 'src/utils/functions/localStorage'
import { shouldBlockForUser } from 'src/utils/functions/sandbox'

import { useSnackData } from './SnackContext'

import { OpenAPI as AppClientOpenAPI } from '@LoopKitchen/app-client'
import { captureException } from '@sentry/react'
import { lokiLogger } from 'src/services/lokiLogger'
import { ChainsService, DefaultService, OpenAPI, TruROIAgentOpenAPI } from 'src/services/openApiV2'
import { userClaimsService } from 'src/services/userClaimsService'
import { sendSlackNotification } from 'src/utils/api'
import { BASE_FRONTEND_URL, LOOPAIXYZ_DOMAIN, LOOPKITCHENXYZ_DOMAIN, TRYLOOPAI_DOMAIN } from 'src/utils/config/config'
import { getSigninToken } from 'src/utils/firebaseFunctions/authFunctions'
import { isInternalUser } from 'src/utils/functions'
import { decrypt, encrypt } from 'src/utils/functions/encryptionDecryption'
import { pwaManager } from 'src/utils/sw/pwaUtils'
import { AccessLevelEnum, AccessLevelType, AuthContextInterface, OrgConfigType, RedirectDialogType, RouteType, User, UserType } from './AuthContext.type'
import { RouteCategoryType } from './PrivateLayoutDrawerContext'
import { usePublicAuthContext } from './PublicAuthContext'
import { SubChainModules, SubChainOrgUser, getSubchainOrgUsers } from './SubChainContext'

const initialState = {} as AuthContextInterface

export const AuthContext = createContext<AuthContextInterface>(initialState)

export const useAuth = (): AuthContextInterface => useContext(AuthContext)

const AuthContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { currentUser: publicCurrentUser, authCurrentUser, analyticsReset, logout: publicLogout, setLoading, magicLinkFromAdmin } = usePublicAuthContext()
  const { openInfo, openError } = useSnackData()
  const [currentUser, setCurrentUser] = useState<User>(publicCurrentUser)
  const [hasMagicLink, setHasMagicLink] = useState(false)
  const { handleError, asyncWrapper } = useErrorData()
  const [orgConfig, setOrgConfig] = useState({} as OrgConfigType)
  const [orgConfigLoading, setOrgConfigLoading] = useState(true)
  const [accessLevels, setAccessLevels] = useState<AccessLevelType[]>([])
  const [userList, setUserList] = useState<UserType[] | null>(null)
  const [userListLoading, setUserListLoading] = useState(true)

  const [defaultRoute, setDefaultRoute] = useState('dashboard')
  const [standaloneRoutesLoaded, setStandaloneRoutesLoaded] = useState(false)
  const [globalRoutes, setGlobalRoutes] = useState<RouteType[]>([])
  const [globalRoutesLoading, setGlobalRoutesLoading] = useState(true)
  const isDemo = (get(currentUser, 'access-level', []) as string[]).includes(AccessLevelEnum.DEMO) || false
  const [redirectLoading, setRedirectLoading] = useState(false)
  const [isCurrentUserSet, setIsCurrentUserSet] = useState(false)
  const [subChainData, setSubchainData] = useState<SubChainOrgUser>(null)
  const [subchainModulesList, setSubchainModulesList] = useState<SubChainModules[]>([])
  const [routeCategories, setRouteCategories] = useState<RouteCategoryType[]>([])
  const [categoryLoading, setCategoryLoading] = useState(true)
  const [categoryOrder, setCategoryOrder] = useState<any[]>([])
  const [openRedirectionDialog, setOpenRedirectionDialog] = useState<RedirectDialogType>({
    openRedirection: false,
    redirectionCallback: () => Promise.resolve(),
    openRedirected: false,
    redirectedCallback: () => Promise.resolve()
  })
  const [isOrgInActive, setIsOrgInActive] = useState(false)

  const subchainModules = useMemo(() => {
    if (!currentUser) return null
    const { uid } = currentUser
    if (subChainData && Array.isArray(subChainData[uid]) && subChainData[uid].length > 0) {
      const { name } = subChainData[uid][0]
      const moduleItem = subchainModulesList.find((item) => item?.name === name)
      if (typeof moduleItem === 'object' && Array.isArray(moduleItem?.modules)) {
        const { modules } = moduleItem
        return modules
      }
    } else {
      return null
    }
  }, [subChainData, currentUser, subchainModulesList])

  const getSubChainRoutes = useCallback(
    (accessLevel: string) => {
      if (
        ![AccessLevelEnum.BUSINESS_ADMIN, AccessLevelEnum.BUSINESS_MANAGER].includes(accessLevel as AccessLevelEnum) &&
        Array.isArray(subchainModules) &&
        subchainModules.length > 0
      ) {
        const defaultAccessLevelConfig = accessLevels.find((item) => item.uid === accessLevel)
        const defaultRoutes: any[] = get(defaultAccessLevelConfig, 'routes', []) || []

        const unCategorizedDefaultRoutes = defaultRoutes.filter((uid) => {
          return routeCategories.every((e) => !e.routes.includes(uid))
        })

        const defaultModules: any[] = get(defaultAccessLevelConfig, 'modules', []) || []
        const defaultModuleRoutes = routeCategories
          .filter((e) => defaultModules.some((uid) => e.uid === uid))
          .reduce(
            (acc, curr) => {
              acc.push(...curr.routes)
              return acc
            },
            [] as typeof defaultRoutes
          )
          .concat(unCategorizedDefaultRoutes)

        const routes = orgConfig.routeDisplayType === 'modules' ? defaultModuleRoutes : defaultRoutes

        const userIsFinanceTeam = accessLevel === AccessLevelEnum.FINANCE_TEAM
        const subchainRoutes = subchainModules
          .map((module) => {
            return routeCategories
              .map((item) => (item.uid === module.uid ? item.routes : []))
              .flat()
              .filter((route) => (userIsFinanceTeam ? true : routes.includes(route)))
              .map((route) => {
                return {
                  uid: route,
                  type: module.type
                }
              })
          })
          .flat()
        return subchainRoutes
      }
      return []
    },
    [subchainModules, routeCategories, accessLevels, orgConfig]
  )

  const getRoutes = useCallback(
    (paramAccessLevel?: AccessLevelEnum) => {
      if (!orgConfig) return []
      const accessLevel = paramAccessLevel || getAccessLevel(currentUser)

      // Check for subchain routes first
      const subChainRoutes = getSubChainRoutes(accessLevel)
      if (subChainRoutes.length > 0) {
        return subChainRoutes
      }

      // Get routes based on display type
      const routes = get(orgConfig, `access_levels.${accessLevel}.routes`, []) as any[]
      const modules: (string | { uid: string; type: 'preview' | 'live' })[] = get(orgConfig, `access_levels.${accessLevel}.modules`, [])
      const useModules = orgConfig?.routeDisplayType === 'modules'

      const finalRoutes = useModules
        ? (() => {
            const unCategorizedRoutes = routes
              .map((item: any) => (typeof item === 'string' ? item : item.uid))
              .filter((routeUid: any) => !routeCategories.some((category) => category.routes.includes(routeUid)))

            return modules
              .flatMap((item) => {
                const moduleUid = typeof item === 'string' ? item : item.uid
                const moduleType = typeof item === 'string' ? 'live' : item.type
                const category = routeCategories.find((cat) => cat.uid === moduleUid)
                return category?.routes.map((route) => ({ uid: route, type: moduleType })) || []
              })
              .concat(unCategorizedRoutes.map((route: any) => ({ uid: route, type: 'live' })))
          })()
        : routes

      // Filter by org-specific rules
      return finalRoutes.filter((item: any) => {
        const routeUid = typeof item === 'string' ? item : item.uid
        const routeObj = globalRoutes.find((route) => route.uid === routeUid)
        if (routeObj?.orgs?.length > 0) {
          return routeObj.orgs_action === 'exclude' ? !routeObj.orgs.includes(currentUser.org) : routeObj.orgs.includes(currentUser.org)
        }
        return true
      })
    },
    [currentUser, orgConfig, routeCategories, globalRoutes, getSubChainRoutes]
  )

  const getWizards = useCallback(
    (paramAccessLevel?: AccessLevelEnum) => {
      if (!orgConfig) return [] as string[]
      const accessLevel = paramAccessLevel || getAccessLevel(currentUser)
      return get(orgConfig, `access_levels.${accessLevel}.wizards`, []) as string[]
    },
    [currentUser, orgConfig]
  )

  function changeStatus(id: string) {
    const userRef = doc(db, 'users', id)
    updateDoc(userRef, { status: deleteField() })
  }

  const getAllAccessLevels = async () => {
    try {
      await setAllAccessLevels()
    } catch (err) {
      console.error('Error in fetching all access levels: ', err)
    }
  }

  const getApiData = async (currUser: any, data: any) => {
    try {
      if (!data || !data?.org) {
        throw new Error("Cannot read properties of null (reading 'org')")
      }
      // setLoading(true)
      if (!isEqual(currentUser, { ...currentUser, ...data })) {
        setCurrentUser({
          ...currentUser,
          ...data
        })
      }
      setIsCurrentUserSet(true)
      changeStatus(currUser.uid)

      // Initialize user claims for API requests
      // This allows backend to read user context from header instead of Firestore
      userClaimsService.setClaims({
        org: data.org,
        'access-level': data['access-level'],
        internal: data.internal
      })

      const cachedOrgConfig = getSession('org_config')
      const cachedDefaultOrgConfig = getSession('default_org_config')
      const cachedRouteCategories = getSession('route_categories')

      let result: any = await Promise.allSettled([
        cachedOrgConfig ?? fetchOrgConfig(data.org, currUser['access-level']),
        cachedDefaultOrgConfig ?? fetchOrgConfig('default', currUser['access-level']),
        cachedRouteCategories ?? fetchFirebaseData('route_categories')
      ]).catch((error) => {
        console.log(error.message)
        if (handleError) handleError(error.message)
      })
      result = result.map((item: any) => item.value)

      if (result[0]) {
        const access_levels = get(result, '[0].access_levels', {})
        const accessLevel = Array.isArray(data['access-level']) ? data['access-level'][0] : data['access-level']
        const defaultRouteForRole = get(
          access_levels,
          `${accessLevel}.defaultRoute.[0]`,
          get(access_levels, `${accessLevel}.routes.[0]`, 'dashboard')
        ) as string
        if (defaultRouteForRole) setDefaultRoute(defaultRouteForRole)
        let navConfig = get(result, `[0].access_levels.[${accessLevel}].routes`, []) as any[]
        const logo = get(result, `[0].logo`, undefined)
        let filterConfig = result[0]?.filterConfig
        if (!navConfig.length) {
          navConfig = result[1]?.navConfig
        }
        if (!filterConfig) {
          filterConfig = result[1]?.filterConfig
        }
        const routeCategories = (result[2] || []) as RouteCategoryType[]
        setRouteCategories(routeCategories)
        const orgData = get(result, '[0]', {}) as any
        const orgConfigData = {
          ...orgData,
          navConfig,
          filterConfig,
          access_levels,
          logo
        }
        setOrgConfig((prev: any) => ({ ...prev, ...orgConfigData }))
        setOrgConfigLoading(false)
        setSession('org_config', orgConfigData)
        localStorage.setItem('storageDate', new Date().toDateString())

        //set data to localstorage
        setSession('data', data)
        setSession('userOrg', data.org)
      } else {
        params.append('error', 'Sorry, the organization name you entered is incorrect. Please check spelling and capitalization and try again.')
        setParams(params)
        logout()
      }
    } catch (error) {
      console.log(error.message)
      if (handleError) handleError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const getHasMagicLink = async () => {
    try {
      const result = (await DefaultService.hasMagicLinkUserHasAdminMagicLinkGet()) as { has_magic_link: boolean }
      setHasMagicLink(!!result?.has_magic_link)
      return !!result?.has_magic_link
    } catch (err) {
      console.error('Error in getting has magic link: ', err)
      return false
    }
  }

  useEffect(() => {
    const getIsOrgInActive = async () => {
      try {
        const res = await ChainsService.getChainStatusChainsStatusGetGet({
          chainName: currentUser.org
        })
        setIsOrgInActive(res !== true)
      } catch (err) {
        console.error('Error in getting chain status: ', err)
        setIsOrgInActive(false)
      }
    }

    getIsOrgInActive()
  }, [currentUser?.org])

  useEffect(() => {
    if (isOrgInActive) {
      navigate('/create-account/wait', { replace: true })
      if (!isInternalUser(publicCurrentUser)) {
        // Log to Loki
        lokiLogger.logAuthEvent('inactive_chain_login_attempt', {
          user: currentUser.email,
          org: currentUser.org,
          accessLevel: getAccessLevel(currentUser),
          url: window.location.href,
          isInternalUser: false
        })

        sendSlackNotification({
          channel: 'inactive-chain-actions',
          message: `User \`${currentUser.email}\` is trying to login to inactive chain (\`${currentUser.org}\`)`,
          title: `User email: \`${currentUser.email}\`\nOrg: \`${currentUser.org}\`\nAccess Level: \`${getAccessLevel(currentUser)}\`\nUser is trying to login to inactive chain\nFrom: \`${window.location.href}\``
        })
      }
    }
  }, [isOrgInActive, isInternalUser(publicCurrentUser)])

  useEffect(() => {
    const getUserData = async () => {
      try {
        const condition = Boolean(authCurrentUser)

        if (condition) {
          // checking if email is present in the url
          const regex = new RegExp('[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+.[A-Za-z]{2,}')
          if (regex.test(window.location.pathname)) {
            navigate('/')
          }
          Sentry.setUser({ email: authCurrentUser.email })

          // let token = null
          // try {
          //   token = await authCurrentUser.getIdToken()
          // } catch (err) {
          //   console.log('error in getting token: ', err)
          // }

          const getToken = async () => {
            try {
              const token = await authCurrentUser.getIdToken()
              setSession('authToken', token, true)
              return token
            } catch (err) {
              console.log('error in getting token: ', err)
              return null
            }
          }

          OpenAPI.TOKEN = getToken
          TruROIAgentOpenAPI.TOKEN = getToken
          AppClientOpenAPI.TOKEN = getToken
          const data = publicCurrentUser

          // Handling observer access level
          if (getAccessLevel({ ...authCurrentUser, ...data }) === AccessLevelEnum.OBSERVER) {
            setLoading(false)
            logout()
            return openError('Login access denied')
          }

          // Handling email verification
          if (data) {
            if (authCurrentUser && !authCurrentUser.emailVerified) {
              setLoading(false)
              logout()
              openInfo('Please verify your email before login.')
              return
            }
            const cachedHasMagicLink = getSession('has_magic_link')
            const userHasMagicLink = cachedHasMagicLink === undefined || cachedHasMagicLink === null ? await getHasMagicLink() : cachedHasMagicLink

            // Vitally tracking is handled in the useEffect hook after currentUser is set
            // The VitallyManager will automatically track the user when initialized
            setLoading(false)
            if (data.stage === 'waiting' && !userHasMagicLink) {
              const waitingLink = '/create-account/wait'
              setCurrentUser({
                ...authCurrentUser,
                ...data
              })
              // User data is already stored in sessionStorage under 'data' key via setSession('data', ...)
              setIsCurrentUserSet(true)
              changeStatus(authCurrentUser.uid)
              if (location.pathname !== waitingLink) {
                navigate(waitingLink)
              }
              setTimeout(() => {
                analyticsReset()
              }, 2000)
              return
            }

            if (data && data?.org) {
              // Check for cached data to unblock UI faster
              const hasCachedOrgConfig = !!getSession('org_config')
              const hasCachedAccessLevels = !!getSession('all_access_levels')
              const hasCachedGlobalRoutes = !!getSession('global_routes')

              if (hasCachedOrgConfig && hasCachedAccessLevels && hasCachedGlobalRoutes) {
                // Use cached data immediately, fetch fresh data in background (non-blocking)
                getApiData(authCurrentUser, data)
                getAllAccessLevels()
                getGlobalRoutes()
              } else {
                // No cache - must wait for data (blocking)
                await Promise.all([getAllAccessLevels(), getGlobalRoutes()])
                await getApiData(authCurrentUser, data)
              }

              if (location.pathname === '/create-account/wait') navigate('/')

              // User data is already stored in sessionStorage under 'data' key via setSession('data', ...)

              // Track successful login/session restoration
              metricsService.trackUserLoginWithSessionVerification()

              setLoading(false)
            } else {
              handleError("Organization information not found. Please contact your organization's admin.")
              setTimeout(() => {
                navigate('/')
                logout()
              })
            }
          } else {
            //create new user entry
            if (authCurrentUser) {
              const newUserObj = {
                name: authCurrentUser.displayName,
                email: authCurrentUser?.email,
                org: null,
                phoneNumber: authCurrentUser.phoneNumber,
                uid: authCurrentUser.uid,
                access_names: [],
                'access-level': [AccessLevelEnum.BUSINESS_ADMIN],
                stage: 'waiting'
              }
              setCurrentUser({
                ...newUserObj,
                stage: 'waiting'
              })
              // User data will be stored in sessionStorage under 'data' key via setSession('data', ...)
              setIsCurrentUserSet(true)
              navigate('/create-account/wait')
              setTimeout(() => {
                analyticsReset()
              }, 2000)
              await setDoc(doc(db, 'users', authCurrentUser.uid), newUserObj)
              changeStatus(authCurrentUser.uid)
              setLoading(false)
            }
          }

          setRedirectLoading(false)
        } else {
          clearList()
          setCurrentUser(undefined)
          setIsCurrentUserSet(true)
          setLoading(false)
          logout()
        }
      } catch (error) {
        setLoading(false)
        if (handleError) {
          if (error.message.includes("Cannot read properties of null (reading 'org')")) {
            handleError('You are not registered with us')
            logout()
          } else {
            handleError(error.message)
          }
        } else {
          console.log(error.message)
        }
      }
    }
    getUserData()
  }, [authCurrentUser])

  useEffect(() => {
    let unsubscribeSpecificOrg: any
    let unsubscribeAccessLevels: any
    let unsubscribeUserList: any
    let unsubscribeSubchainModules: any
    let unsubscribeRoutes: any
    let unsubscribeCurrentUser: any
    let unsubscribeOrgConfig: any
    let unsubscribeDefaultOrgConfig: any
    let unsubscribeHasMagicLink: any

    if (currentUser && currentUser.org) {
      const accessLevel = getAccessLevel(currentUser)
      if (accessLevel === AccessLevelEnum.OBSERVER) {
        logout()
        openError('Login access denied')
      }
      try {
        const q = doc(db, `org_config_2/${currentUser.org}`)
        unsubscribeSpecificOrg = onSnapshot(
          q,
          (snapShot) => {
            const tempOrgConfig = getSession('org_config')
            if (tempOrgConfig) {
              const version1 = get(tempOrgConfig, 'version', 0)
              const data = snapShot.data()
              const version2 = get(data, 'version', 0)
              setOrgConfig((prev: any) => ({ ...prev, ...data }))
              setOrgConfigLoading(false)

              setSession('org_config', data)
              if (version2.toString() !== version1.toString()) {
                openInfo('Your access level is updated by your organization')
              }
            }
          },
          (error) => {
            console.error('Firestore listener error for org config:', error)
            // Don't logout - org config errors shouldn't break the app
          }
        )
      } catch (error: any) {
        console.log(error.message)
      }

      try {
        const q = collection(db, 'access_levels')
        unsubscribeAccessLevels = onSnapshot(q, (querySnapshot) => {
          // let isCustomAccessLevel  = false
          const documents = querySnapshot.docs.map((doc) => {
            const data = doc.data()
            return { ...data, uid: doc.id, label: data.name, value: doc.id }
          })
          setAccessLevels(documents as AccessLevelType[])
          const docs = querySnapshot.docs.map((doc) => {
            const data = doc.data()
            // Only set isCustomFix if this is the current user's access level
            if (doc.id === getAccessLevel(currentUser)) {
              // isCustomAccessLevel  = data?.is_custom
            }
            return {
              name: data?.name,
              label: data?.label,
              is_custom: data?.is_custom
            }
          })
          setSession('all_access_levels', docs)

          // Now that we know if it's a custom access level, set up the userList listener
          // We need to handle unsubscribeUserList properly for cleanup
          const setupUserListListener = () => {
            try {
              if (shouldBlockForUser(currentUser)) {
                setUserList([])
                setUserListLoading(false)
              } else if (!currentUser.org) {
                // console.log('ERROR: currentUser.org is undefined or null!', currentUser)
                setUserList([])
                setUserListLoading(false)
              } else {
                // console.log('Creating Firebase query with org:', currentUser.org)
                const docRef = query(collection(db, 'users'), where('org', '==', currentUser.org))

                const currentAccessLevel = getAccessLevel(currentUser)
                // Check if it's a custom access level (from Firebase data

                // Fetch userList for management roles AND custom access levels
                const shouldFetchUserList = [AccessLevelEnum.BUSINESS_ADMIN, AccessLevelEnum.BUSINESS_MANAGER, AccessLevelEnum.INTERNAL_OPERATOR].includes(
                  currentAccessLevel
                )

                // console.log('shouldFetchUserList:', shouldFetchUserList, 'for user:', currentUser.email)

                if (shouldFetchUserList) {
                  // Clear any existing listener before setting a new one
                  if (unsubscribeUserList) {
                    unsubscribeUserList()
                  }

                  // First, do a one-time batch fetch for immediate rendering
                  setUserListLoading(true)
                  getDocs(docRef)
                    .then((querySnapshot) => {
                      const documents: UserType[] = querySnapshot.docs.map((doc) => {
                        const data = doc.data()
                        return { ...data, access_level: data['access-level'], uid: doc.id } as UserType
                      })

                      setUserList(documents)
                      setUserListLoading(false)

                      // After initial load, set up real-time listener for updates
                      unsubscribeUserList = onSnapshot(
                        docRef,
                        (updateSnapshot) => {
                          const updatedDocuments: UserType[] = updateSnapshot.docs.map((doc) => {
                            const data = doc.data()
                            return { ...data, access_level: data['access-level'], uid: doc.id } as UserType
                          })
                          setUserList(updatedDocuments)
                        },
                        (error) => {
                          console.error('Error in user list real-time updates:', error)
                        }
                      )
                    })
                    .catch((error) => {
                      setUserListLoading(false)
                      console.error('Error fetching initial user list:', error)
                    })
                } else {
                  // console.log('Not fetching userList for access level:', currentAccessLevel)
                  // For non-admin users, add only the current user to the list
                  setUserList([
                    {
                      ...currentUser,
                      access_level: currentUser['access-level'],
                      uid: currentUser.uid
                    } as UserType
                  ])
                  setUserListLoading(false)
                }
              }
            } catch (err) {
              console.log('user list error: ', err)
              setUserListLoading(false)
            }
          }

          // Only setup listener once - check if we already have a listener or are loading
          // This prevents multiple calls which cause the flash
          if (!unsubscribeUserList && userList === null) {
            setupUserListListener()
          }
        })
      } catch (err) {
        console.log('access level error: ', err)
      }

      try {
        const q = query(collection(db, 'subchain_modules'), where('org', '==', currentUser.org))
        unsubscribeSubchainModules = onSnapshot(q, (querySnapshot) => {
          const documents = querySnapshot.docs.map((doc) => {
            const data = doc.data()
            return { ...data, uid: doc.id } as SubChainModules
          })
          setSubchainModulesList(documents)
        })
      } catch (err) {
        console.error('error in fetching subchain modules', err)
      }

      try {
        unsubscribeDefaultOrgConfig = onSnapshot(doc(db, 'org_config_2', 'default'), (snapshot) => {
          const data = snapshot.data()
          setSession('default_org_config', data)
        })
      } catch (err) {
        console.error('error in fetching default org config', err)
      }

      try {
        unsubscribeCurrentUser = onSnapshot(
          doc(db, 'users', currentUser.uid),
          async (snapshot) => {
            const data = snapshot.data()
            if (!data) return // Exit early if no data

            // Handle user data update
            setCurrentUser((prev) => {
              if (!prev) return prev // Exit if no previous user

              // Handle clearCacheAt update
              if (prev.clearCacheAt !== data.clearCacheAt && data.clearCacheAt) {
                console.log('Cache clear requested:', prev.clearCacheAt, '->', data.clearCacheAt)
                pwaManager.cache
                  .clearAllCaches()
                  .then(() => {
                    console.log('Cache cleared successfully')
                  })
                  .catch((error) => {
                    console.error('Failed to clear cache:', error)
                  })
              }

              const newData = {
                ...prev,
                ...data
              }
              if (!isEqual(newData, prev)) {
                // Update claims if relevant fields changed (org, access-level, internal)
                if (userClaimsService.needsUpdate(newData)) {
                  userClaimsService.setClaims({
                    org: newData.org,
                    'access-level': newData['access-level'],
                    internal: newData.internal
                  })
                }
                return newData
              }
              return prev
            })
          },
          (error) => {
            // Error callback for onSnapshot
            console.error('Firestore listener error for user data:', error)
            // Don't logout on permission errors - they might be temporary
          }
        )
      } catch (err) {
        console.error('error in setting up user data listener', err)
      }

      try {
        unsubscribeRoutes = onSnapshot(collection(db, 'route_categories'), (snapshot) => {
          const data = snapshot.docs.map((doc) => doc.data()) as RouteCategoryType[]
          setRouteCategories(data)
          setSession('route_categories', data)
        })
      } catch (err) {
        console.error('error in fetching route categories', err)
      }

      try {
        const magic_link_query = query(collection(db, 'users_magic_link'), where('user_uid', '==', currentUser.uid))
        unsubscribeHasMagicLink = onSnapshot(magic_link_query, (snapshot) => {
          const docs = snapshot.docs
          const data = Array.isArray(docs) && docs.length > 0 ? docs[0].data() : null
          if (data && get(data, 'forAdmin', false) && get(data, 'magic_link.token', '')) {
            setHasMagicLink(true)
            setSession('has_magic_link', true)
          } else {
            setHasMagicLink(false)
            setSession('has_magic_link', false)
          }
        })
      } catch (err) {
        console.error('error in fetching magic link', err)
      }
    }
    return () => {
      if (unsubscribeSpecificOrg) {
        unsubscribeSpecificOrg()
      }
      if (unsubscribeAccessLevels) {
        unsubscribeAccessLevels()
      }
      if (unsubscribeUserList) {
        unsubscribeUserList()
      }
      if (unsubscribeSubchainModules) {
        unsubscribeSubchainModules()
      }
      if (unsubscribeRoutes) unsubscribeRoutes()
      if (unsubscribeCurrentUser) unsubscribeCurrentUser()
      if (unsubscribeOrgConfig) unsubscribeOrgConfig()
      if (unsubscribeDefaultOrgConfig) unsubscribeDefaultOrgConfig()
      if (unsubscribeHasMagicLink) unsubscribeHasMagicLink()
    }
  }, [currentUser])

  const refreshSubChainData = useCallback(async () => {
    try {
      if (currentUser && currentUser?.org) {
        const res = await getSubchainOrgUsers(currentUser.org)
        setSubchainData(res)
      }
    } catch (err) {
      console.log('error in refreshing franchisee group data', err)
      handleError('Something went wrong while refreshing franchisee group data')
    }
  }, [currentUser?.org])

  useEffect(() => {
    if (currentUser && currentUser?.org) {
      refreshSubChainData()
    }
  }, [currentUser?.org, refreshSubChainData])

  function posthogCapture(label: string, data: any = {}) {
    try {
      posthog.capture(label, {
        email: currentUser?.email,
        name: currentUser?.name,
        org: currentUser?.org,
        time: new Date().toISOString(),
        ...data
      })
    } catch (error) {
      handleError(error.message)
    }
  }

  const getGlobalRoutes = async () => {
    try {
      const cachedGlobalRoutes = getSession('global_routes')
      if (cachedGlobalRoutes) {
        setGlobalRoutes(cachedGlobalRoutes)
        setGlobalRoutesLoading(false)
        return
      }
      const snapshotData = await fetchFirebaseData('routes')
      const tempRoutes =
        snapshotData?.map(
          (data: any) =>
            ({
              ...data,
              uid: data.id
            }) as RouteType
        ) || []

      setGlobalRoutes(tempRoutes)
      setSession('global_routes', tempRoutes)
      setGlobalRoutesLoading(false)
    } catch (err) {
      console.error('global routes error: ', err)
    }
  }

  useEffect(() => {
    let unsubscribeGlobalRoutes: any

    try {
      unsubscribeGlobalRoutes = onSnapshot(collection(db, 'routes'), (snapshot) => {
        const tempRoutes = snapshot.docs.map(
          (doc) =>
            ({
              ...doc.data(),
              uid: doc.id
            }) as RouteType
        )

        setGlobalRoutes(tempRoutes)
        setSession('global_routes', tempRoutes)
        setGlobalRoutesLoading(false)
      })
    } catch (err) {
      console.error('Error in fetching global routes ', err)
      setGlobalRoutes([])
      setGlobalRoutesLoading(false)
    }

    return () => {
      if (unsubscribeGlobalRoutes) {
        unsubscribeGlobalRoutes()
      }
    }
  }, [])

  useEffect(() => {
    const getRouteCategories = async () => {
      try {
        const routeCategoryOrderSnapshot = await fetchFirebaseData('configs', 'route_categories')
        const categoryOrder = routeCategoryOrderSnapshot?.order || []
        setCategoryOrder(categoryOrder)
        setCategoryLoading(false)
      } catch (err) {
        console.error('Error in getRouteCategories', err)
      }
    }
    getRouteCategories()
    let routeCategoryOrder = false
    const setCategoryLoadingFalse = (obj: { type: 'route_category_order' }) => {
      if (obj.type === 'route_category_order') {
        routeCategoryOrder = true
      }
      if (routeCategoryOrder) {
        setCategoryLoading(false)
      }
    }

    const unsubscribeCategoryOrder = onSnapshot(doc(db, 'configs/route_categories'), (snapshot) => {
      const categories = snapshot.data()?.order || []
      setCategoryOrder(categories)
      setCategoryLoadingFalse({ type: 'route_category_order' })
    })

    return () => {
      unsubscribeCategoryOrder()
    }
  }, [])

  const logout = () => {
    asyncWrapper(
      (async () => {
        posthogCapture('User logged out')

        // Clear user claims for API requests
        userClaimsService.clearClaims()

        // Sign out immediately for fast logout UX
        // User data cleared from sessionStorage via publicLogout which clears the 'data' key
        publicLogout()

        // Clear caches in background after logout completes
        // This prevents blocking the logout flow while still cleaning up browser data
        setTimeout(() => {
          pwaManager.cache.clearAllCaches(false, true).catch((err) => {
            console.error('Background cache clear failed:', err)
          })
        }, 100)
      })()
    )
  }

  // fetch initial cache from local storage
  useEffect(() => {
    const cacheConfig = [
      { key: 'data', setter: setCurrentUser },
      { key: 'route_categories', setter: setRouteCategories },
      { key: 'org_config', setter: setOrgConfig },
      { key: 'all_access_levels', setter: setAccessLevels },
      { key: 'has_magic_link', setter: setHasMagicLink }
    ]

    cacheConfig.forEach(({ key, setter }) => {
      const cached = getSession(key)
      if (cached != null) setter(cached)
    })

    // Initialize claims from cached user data if available
    const cachedUserData = getSession('data')
    if (cachedUserData?.org) {
      userClaimsService.setClaims({
        org: cachedUserData.org,
        'access-level': cachedUserData['access-level'],
        internal: cachedUserData.internal
      })
    }
  }, [])

  useEffect(() => {
    if (isCurrentUserSet && currentUser) {
      vitallyManager.initialize(currentUser, magicLinkFromAdmin).catch((error) => {
        console.error('Failed to initialize Vitally:', error)
        captureException(error)
      })
    }
  }, [isCurrentUserSet, currentUser, magicLinkFromAdmin])

  useEffect(() => {
    if (!currentUser) return

    const claimObject = {
      org: currentUser.org,
      'access-level': getAccessLevel(currentUser),
      internal: currentUser.internal
    }

    if (userClaimsService.needsUpdate(claimObject)) {
      userClaimsService.setClaims({
        org: currentUser.org,
        'access-level': getAccessLevel(currentUser),
        internal: currentUser.internal
      })
    }
  }, [currentUser])

  const signInWithIdToken = async (idToken: string) => {
    const signInData = await getSigninToken({ idToken })

    if (signInData && signInData.success) {
      const customToken = signInData.token
      await signInWithCustomToken(auth, customToken)
    } else {
      openError('Failed to create session')
    }
  }

  const redirectUsersToTryloop = async () => {
    try {
      const location = window.location.href
      const host = window.location.host
      const isTryloopDomain = host.includes(TRYLOOPAI_DOMAIN)
      const isLoopkitchenDomain = host.includes(LOOPKITCHENXYZ_DOMAIN) || host.includes(LOOPAIXYZ_DOMAIN)
      const shortLocation = location.slice(10)
      const path = shortLocation.slice(shortLocation.indexOf('/'))

      if (isTryloopDomain) {
        const redirected = params.get('redirected') || false

        if (redirected) {
          setParams((prev) => {
            prev.delete('redirected')
            return prev
          })

          setOpenRedirectionDialog((prev) => ({
            ...prev,
            openRedirected: true,
            redirectedCallback: async () => {
              const encryptedIdToken = params.get('id_token')
              const name = get(currentUser, 'name', '-')
              const email = get(currentUser, 'email', '-')
              const org = get(currentUser, 'org', '-')

              if (encryptedIdToken) {
                const idToken = decrypt(encryptedIdToken)

                if (idToken) {
                  // Log to Loki
                  lokiLogger.logAuthEvent('user_redirected_to_tryloop', {
                    loggedIn: !!name,
                    name: name,
                    email: email,
                    org: org,
                    hasIdToken: true,
                    url: window.location.href
                  })

                  await sendSlackNotification({
                    message: `\`Redirected\` user to Tryloop domain`,
                    title: `Details: \n\t Logged in: \`${!!name}\` \n\t Name: \`${name}\` \n\t Email: \`${email}\` \n\t Org: \`${org}\` \n\t Id Token: \`${idToken}\``,
                    channel: 'fe-auth-redirect-logs'
                  })

                  posthogCapture('User redirected to Tryloop domain', {
                    name,
                    email,
                    org,
                    idToken
                  })

                  setRedirectLoading(true)
                  await signInWithIdToken(idToken)
                }
              } else {
                // Log to Loki
                lokiLogger.logAuthEvent('user_redirected_to_tryloop', {
                  loggedIn: !!name,
                  name: name,
                  email: email,
                  org: org,
                  hasIdToken: false,
                  url: window.location.href
                })

                await sendSlackNotification({
                  message: `\`Redirected\` user to Tryloop domain`,
                  title: `Details: \n\t Logged in: \`${!!name}\` \n\t Name: \`${name}\` \n\t Email: \`${email}\` \n\t Org: \`${org}\` \n\t Id Token: \`-\``,
                  channel: 'fe-auth-redirect-logs'
                })

                posthogCapture('User redirected to Tryloop domain', {
                  name,
                  email,
                  org
                })
              }
            }
          }))
        }
      } else {
        if (isLoopkitchenDomain) {
          if (!isCurrentUserSet) return

          setOpenRedirectionDialog((prev) => ({
            ...prev,
            openRedirection: true,
            redirectionCallback: async () => {
              setRedirectLoading(true)

              if (currentUser) {
                const idToken = await auth.currentUser.getIdToken()
                const encryptedIdToken = encrypt(idToken)

                const redirectUrl = `${BASE_FRONTEND_URL}/?redirect=${path}&id_token=${encryptedIdToken}&redirected=true`

                // Log to Loki
                lokiLogger.logAuthEvent('user_redirecting_to_tryloop', {
                  loggedIn: true,
                  name: currentUser.name,
                  email: currentUser.email,
                  org: currentUser.org,
                  hasIdToken: true,
                  url: window.location.href
                })

                await sendSlackNotification({
                  message: `\`Redirecting user\` to Tryloop domain`,
                  title: `Details: \n\t Logged in: \`true\` \n\t Name: \`${currentUser.name}\` \n\t Email: \`${currentUser.email}\` \n\t Org: \`${currentUser.org}\` \n\t Id Token: \`${idToken}\``,
                  channel: 'fe-auth-redirect-logs'
                })

                posthogCapture('User redirecting to Tryloop domain', {
                  name: currentUser.name,
                  email: currentUser.email,
                  org: currentUser.org,
                  idToken
                })

                logout()
                window.location.href = redirectUrl
              } else {
                const redirectUrl = `${BASE_FRONTEND_URL}/?redirect=${path}&redirected=true`

                // Log to Loki
                lokiLogger.logAuthEvent('user_redirecting_to_tryloop', {
                  loggedIn: false,
                  name: null,
                  email: null,
                  org: null,
                  hasIdToken: false,
                  url: window.location.href
                })

                await sendSlackNotification({
                  message: `\`Redirecting user\` to Tryloop domain`,
                  title: `Details: \n\t Logged in: \`false\` \n\t Name: \`-\` \n\t Email: \`-\` \n\t Org: \`-\` \n\t Id Token: \`-\``,
                  channel: 'fe-auth-redirect-logs'
                })

                posthogCapture('User redirecting to Tryloop domain')

                window.location.href = redirectUrl
              }

              setRedirectLoading(false)
            }
          }))
        }
      }
    } catch (err) {
      handleError(err)
    }
  }

  useEffect(() => {
    redirectUsersToTryloop()
  }, [currentUser, isCurrentUserSet])

  const contextValue = useMemo(
    () => ({
      currentUser,
      logout,
      orgConfig,
      orgConfigLoading,
      accessLevels,
      userList,
      userListLoading,
      defaultRoute,
      setDefaultRoute,
      setCurrentUser,
      isDemo,
      posthogCapture,
      getApiData,
      hasMagicLink,
      globalRoutes,
      globalRoutesLoading,
      standaloneRoutesLoaded,
      setStandaloneRoutesLoaded,
      redirectLoading,
      openRedirectionDialog,
      setOpenRedirectionDialog,
      getSubChainRoutes,
      getRoutes,
      getWizards,
      isOrgInActive,
      subChainData,
      refreshSubChainData,
      routeCategories,
      categoryLoading,
      categoryOrder
    }),
    [
      currentUser,
      logout,
      orgConfig,
      orgConfigLoading,
      accessLevels,
      userList,
      userListLoading,
      defaultRoute,
      setDefaultRoute,
      setCurrentUser,
      isDemo,
      posthogCapture,
      getApiData,
      hasMagicLink,
      globalRoutes,
      globalRoutesLoading,
      standaloneRoutesLoaded,
      setStandaloneRoutesLoaded,
      redirectLoading,
      openRedirectionDialog,
      setOpenRedirectionDialog,
      getSubChainRoutes,
      getRoutes,
      getWizards,
      isOrgInActive,
      subChainData,
      refreshSubChainData,
      routeCategories,
      categoryLoading,
      categoryOrder
    ]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthContextProvider
